import React from 'react';
import { Field } from 'redux-form';
import { required, printable, notEmpty } from '../../../utils/validate';
import { formatAmount, toNumber } from '../../../utils/formatters';
import OptionList, { YesOrNoOptionList } from '../../Forms/OptionList/optionList';
import Input from '../../Forms/Input/input';
import Select from '../../Forms/Select/select';
import FeatureToggle from '../../FeatureToggle/feature.toggle';
import PropTypes from 'prop-types';

const doesRequireEmployer = (occupation) => occupation && !['', 'Student', 'Unemployed', 'Other', 'Homemaker', 'Retired', 'Other/Not Listed'].includes(occupation);

const doesRequireEmployerWithoutOther = (occupation) => occupation && !['', 'Student', 'Unemployed', 'Other', 'Homemaker', 'Retired'].includes(occupation);

const mainOccupationsList = [
  'Accountant',
  'Administration',
  'Agriculture',
  'Antiquities/Auctions',
  'Architect',
  'Arms/Ammunition Dealer',
  'Artist/Entertainer',
  'ATM Owner/Operator',
  'Attorney/Judge/Legal Services',
  'Cashier/Clerk/Server',
  'Casino/Gaming',
  'Charitable Organization',
  'Clergy/Pastor',
  'Construction/Mining/Oil/Gas',
  'Convenience Store Mgr/Owner',
  'Doctor/Dentist/Pharmacist',
  'Education',
  'Embassy/Foreign Consulate',
  'Engineer/Scientist',
  'Executive Professional',
  'Finance/Banking',
  'Gas Station/Liquor Store Owner',
  'Government Employee/Politician',
  'Health/Fitness',
  'Hospitality/Travel',
  'Import/Export',
  'Insurance/Real Estate',
  'Jeweler/Gem/Metal Dealer',
  'Maintenance/Cleaning/Other Labor',
  'Management Professional',
  'Manufacturing',
  'Money Transmitter/Check Cashing',
  'Other/Not Listed',
  'Parking Lot/Garage Owner',
  'Pawn Broker',
  'Pilot',
  'Police/Fire/EMT/Military',
  'Restaurant Employee',
  'Retail',
  'Sales',
  'Securities/Investment Advisor',
  'Social Worker',
  'Technology',
  'Trade/Plumber/Electrician',
  'Transportation/Trucking',
  'Vehicle Sales (Auto/Plane/Boat)',
  'Vending Machine Operator'
];

const fullOccupationsList = mainOccupationsList.concat([
  'Homemaker',
  'Retired',
  'Student',
  'Unemployed'
]).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

const occupationsMap = window.isToggleOn('cl_source_of_income_retirement') ? {
  Disability: fullOccupationsList.map(o => ({ value: o, text: o })),
  Employed: mainOccupationsList.map(o => ({ value: o, text: o })),
  Homemaker: [{ value: 'Homemaker', text: 'Homemaker' }],
  Other: fullOccupationsList.map(o => ({ value: o, text: o })),
  Retirement: [{ value: 'Retired', text: 'Retired' }],
  'Self-employed': mainOccupationsList.map(o => ({ value: o, text: o })),
  'Social Security': fullOccupationsList.map(o => ({ value: o, text: o })),
  Unemployed: [{ value: 'Unemployed', text: 'Unemployed' }]
} : {
  Disability: fullOccupationsList.map(o => ({ value: o, text: o })),
  Employed: mainOccupationsList.map(o => ({ value: o, text: o })),
  Homemaker: [{ value: 'Homemaker', text: 'Homemaker' }],
  Other: fullOccupationsList.map(o => ({ value: o, text: o })),
  Retired: [{ value: 'Retired', text: 'Retired' }],
  'Self-employed': mainOccupationsList.map(o => ({ value: o, text: o })),
  'Social Security': fullOccupationsList.map(o => ({ value: o, text: o })),
  Unemployed: [{ value: 'Unemployed', text: 'Unemployed' }]
};

const amlOccupations = fullOccupationsList.map(o => ({ value: o, text: o }));

export const EmploymentFieldSet = (props) => {
  const { displayMiniAppFields, displayFullAppFields, occupation, initialValues, sourceOfIncome, updateFormValue } = props;
  const occupationsOptions = occupationsMap[sourceOfIncome] || [];
  const sourceOfIncomeOptions = Object.keys(occupationsMap).map(o => ({ value: o, text: o }));
  const occupationsFieldDisabled = occupationsOptions.length < 2;
  const requiresEmployer = doesRequireEmployer(occupation);
  const requiresEmployerWithoutOther= doesRequireEmployerWithoutOther(occupation);

  const resetOccupation = (e, value) => {
    const occupationsOptions = occupationsMap[value] || [];
    let occupation = '';
    if (occupationsOptions.length === 1) {
      occupation = occupationsOptions[0].value;
    }
    updateFormValue('occupation', occupation);
  };

  return (
    <React.Fragment>
      <FeatureToggle featureName="cl_soi_field">
        <FeatureToggle featureName="cl_display_employment_section_for_soi">
          {((displayFullAppFields && requiresEmployerWithoutOther) || displayMiniAppFields) &&
            // don't show Employment section for fullApp when no employment needed
            <fieldset className="container">
              <legend className="row">Employment</legend>
              { displayMiniAppFields &&
                <div className="row">

                  <div className="col-12 col-lg-6">
                    <Field
                      name="sourceOfIncome"
                      label="Source of income"
                      component={Select}
                      type="select"
                      defaultValue=""
                      validate={[required]}
                      required={true}
                      onChange={(e, v) => { resetOccupation(e, v); }}
                    >
                      <OptionList options={ sourceOfIncomeOptions } />
                    </Field>
                  </div>
                  <div className="col-12 col-lg-6">
                    <Field
                      name="occupation"
                      label="Occupation"
                      component={Select}
                      type="select"
                      defaultValue=""
                      validate={[required]}
                      required={true}
                      disabled={occupationsFieldDisabled}
                    >
                      <OptionList
                        options={ occupationsOptions }
                        createDefaultOption={occupationsOptions.length !== 1}
                      />
                    </Field>
                  </div>
                </div>
              }
              { (displayFullAppFields && requiresEmployerWithoutOther) &&
                <div className="row">
                  <React.Fragment>
                    <div className="col-12 col-lg-6">
                      <Field
                        name="employerName"
                        label="Employer Name"
                        component={Input}
                        type="text"
                        validate={[required, printable, notEmpty]}
                        htmlAttributes={{ maxLength: "40" }}
                      />
                    </div>
                    <div className="col-12 col-lg-6">
                      <Field
                        name="yearsAtCurrentEmployer"
                        label="Years at current employer"
                        component={Input}
                        type="text"
                        validate={[required]}
                        normalize={toNumber}
                        htmlAttributes={{ maxLength: "2" }}
                      />
                    </div>
                  </React.Fragment>
                </div>
              }
              { displayMiniAppFields &&
                <div className="row">
                  <div className="col-12 col-lg-6">
                    <Field
                      name="grossAnnualIncome"
                      label="Total annual gross income"
                      component={Input}
                      type="text"
                      format={formatAmount}
                      normalize={toNumber}
                      validate={[required]}
                      required={true}
                      htmlAttributes={{ maxLength: "12" }}
                      smallDisclosure={{
                        text: "Gross annual income may include sources such as wages, retirement income, investments. You don’t need to include alimony, child support, or separate maintenance income if you don’t want to consider it a basis for repayment.",
                        active: !!initialValues.grossAnnualIncome
                      }}
                      postfix=".00"
                    />
                  </div>
                </div>
              }
            </fieldset>
          }
        </FeatureToggle>
        <FeatureToggle featureName="cl_display_employment_section_for_soi" showWhenFeatureToggleOff={true}>
          { ((displayFullAppFields && requiresEmployer) || displayMiniAppFields) &&
          // don't show Employment section for fullApp when no employment needed
          <fieldset className="container">
            <legend className="row">Employment</legend>
            { displayMiniAppFields &&
              <div className="row">

                <div className="col-12 col-lg-6">
                  <Field
                    name="sourceOfIncome"
                    label="Source of income"
                    component={Select}
                    type="select"
                    defaultValue=""
                    validate={[required]}
                    required={true}
                    onChange={(e, v) => { resetOccupation(e, v); }}
                  >
                    <OptionList options={ sourceOfIncomeOptions } />
                  </Field>
                </div>
                <div className="col-12 col-lg-6">
                  <Field
                    name="occupation"
                    label="Occupation"
                    component={Select}
                    type="select"
                    defaultValue=""
                    validate={[required]}
                    required={true}
                    disabled={occupationsFieldDisabled}
                  >
                    <OptionList
                      options={ occupationsOptions }
                      createDefaultOption={occupationsOptions.length !== 1}
                    />
                  </Field>
                </div>
              </div>
            }
            { (displayFullAppFields && requiresEmployer) &&
              <div className="row">
                <React.Fragment>
                  <div className="col-12 col-lg-6">
                    <Field
                      name="employerName"
                      label="Employer Name"
                      component={Input}
                      type="text"
                      validate={[required, printable, notEmpty]}
                      htmlAttributes={{ maxLength: "40" }}
                    />
                  </div>
                  <div className="col-12 col-lg-6">
                    <Field
                      name="yearsAtCurrentEmployer"
                      label="Years at current employer"
                      component={Input}
                      type="text"
                      validate={[required]}
                      normalize={toNumber}
                      htmlAttributes={{ maxLength: "2" }}
                    />
                  </div>
                </React.Fragment>
              </div>
            }
            { displayMiniAppFields &&
              <div className="row">
                <div className="col-12 col-lg-6">
                  <Field
                    name="grossAnnualIncome"
                    label="Total annual gross income"
                    component={Input}
                    type="text"
                    format={formatAmount}
                    normalize={toNumber}
                    validate={[required]}
                    required={true}
                    htmlAttributes={{ maxLength: "12" }}
                    smallDisclosure={{
                      text: "Gross annual income may include sources such as wages, retirement income, investments. You don’t need to include alimony, child support, or separate maintenance income if you don’t want to consider it a basis for repayment.",
                      active: !!initialValues.grossAnnualIncome
                    }}
                    postfix=".00"
                  />
                </div>
              </div>
            }
          </fieldset>
          }
        </FeatureToggle>
      </FeatureToggle>

      <FeatureToggle featureName="cl_soi_field" showWhenFeatureToggleOff={true}>
        <fieldset className="container">
          <legend className="row">Employment</legend>
          { displayMiniAppFields &&
            <div className="row">

              <div className="col-12 col-lg-6">
                <Field
                  name="occupation"
                  label="Occupation"
                  component={Select}
                  type="select"
                  defaultValue=""
                  validate={[required]}
                  required={true}
                >
                  <OptionList options={ amlOccupations } />
                </Field>
              </div>

              <div className="col-12 col-lg-6">
                <Field
                  name="grossAnnualIncome"
                  label="Total annual gross income"
                  component={Input}
                  type="text"
                  format={formatAmount}
                  normalize={toNumber}
                  validate={[required]}
                  required={true}
                  htmlAttributes={{ maxLength: "12" }}
                  smallDisclosure={{
                    text: "Gross annual income may include sources such as wages, retirement income, investments. You don’t need to include alimony, child support, or separate maintenance income if you don’t want to consider it a basis for repayment.",
                    active: !!initialValues.grossAnnualIncome
                  }}
                  postfix=".00"
                />
              </div>
            </div>
          }

          <div className="row">
            { (displayFullAppFields && requiresEmployer) &&
              <div className="col-12 col-lg-6">
                <Field
                  name="employerName"
                  label="Employer Name"
                  component={Input}
                  type="text"
                  validate={[required, printable, notEmpty]}
                  htmlAttributes={{ maxLength: "40" }}
                />
              </div>
            }
          </div>
          <div className="row">
            { (displayFullAppFields && requiresEmployer) &&
              <div className="col-12 col-lg-6">
                <Field
                  name="yearsAtCurrentEmployer"
                  label="Years at current employer"
                  component={Input}
                  type="text"
                  validate={[required]}
                  normalize={toNumber}
                  htmlAttributes={{ maxLength: "2" }}
                />
              </div>
            }
            { (displayFullAppFields) &&
              <div className="col-12 col-lg-6">
                <Field
                  name="selfEmployed"
                  label="Are you self employed"
                  component={Select}
                  type="text"
                  validate={[required]}
                  required={true}
                >
                  <YesOrNoOptionList />
                </Field>
              </div>
            }
          </div>
        </fieldset>
      </FeatureToggle>
    </React.Fragment>
  );
};

EmploymentFieldSet.defaultProps = {
  displayMiniAppFields: true,
  displayFullAppFields: true,
  initialValues: {}
};

EmploymentFieldSet.propTypes = {
  displayMiniAppFields: PropTypes.bool,
  displayFullAppFields: PropTypes.bool,
  sourceOfIncome: PropTypes.string,
  occupation: PropTypes.string
};

export default EmploymentFieldSet;
